import React from 'react';
import ExternalFactsheetLink from './ExternalFactsheetLink';

/**
 * KD-3581 §3 — the full-width band that opens an experience frame.
 *
 * It is the ONLY place an experience price is shown (§9): the days it covers
 * never repeat it, which is what makes the total readable at a glance. The
 * factsheet icon lives here too, and only here, since the days that repeat the
 * name would repeat the icon with it.
 *
 * Every label arrives formatted — amounts included. Currency formatting stays
 * in the apps, next to the trip currency, so nothing is computed here.
 *
 * Geometry and type sizes are those measured on the mockup: a 54px thumbnail,
 * a 17px name, and a right column where the total is the largest orange of the
 * whole timeline — it is the number the composer scans for.
 *
 * The row wraps rather than crushes. Side by side with a version the panel is
 * half as wide, and the amounts are the one thing that cannot shrink — they are
 * `whitespace-nowrap` on purpose. Left to shrink against them the name ended up
 * one letter per line, so it keeps a floor of its own and the money column —
 * the total, the per-person line and the removal cross, which travel together —
 * drops to a second line when what is left is not enough for both.
 */
type TripExperienceFrameHeaderProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'className'
> & {
  name: string;
  /** Opens the experience factsheet in a new tab. The name stays inert. */
  factsheetUrl?: string;
  factsheetLabel?: string;
  /** Apps pass their own placeholder when the experience has no picture. */
  imageUrl?: string;
  imageAlt?: string;
  /** "Day 1–6", or "Day 7" on a single-day experience. */
  dayRangeLabel?: string;
  /** "Hanoi → Hue". */
  itineraryLabel?: React.ReactNode;
  /** Total price of the experience, already formatted. */
  priceLabel?: React.ReactNode;
  pricePerPersonLabel?: React.ReactNode;
  onRemove?: React.MouseEventHandler<HTMLButtonElement>;
  removeLabel?: string;
};

const TripExperienceFrameHeader = React.forwardRef<
  HTMLElement,
  TripExperienceFrameHeaderProps
>(
  (
    {
      name,
      factsheetUrl,
      factsheetLabel,
      imageUrl,
      imageAlt = '',
      dayRangeLabel,
      itineraryLabel,
      priceLabel,
      pricePerPersonLabel,
      onRemove,
      removeLabel,
      ...props
    },
    ref,
  ) => (
    <header
      ref={ref}
      className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 border-0 border-b border-solid border-[#E7ECF3] bg-white px-4 py-3.5"
      {...props}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={imageAlt}
          className="h-[54px] w-[54px] shrink-0 rounded-[10px] object-cover"
        />
      )}

      <div className="min-w-[9rem] flex-1">
        <div className="text-[17px] font-bold leading-[1.3] tracking-[-0.01em] text-[#16284F]">
          <ExternalFactsheetLink
            name={name}
            href={factsheetUrl}
            label={factsheetLabel}
          />
        </div>

        {(dayRangeLabel || itineraryLabel) && (
          <div className="mt-[3px] flex flex-wrap items-center gap-x-1.5 text-[13.5px] text-slate-500">
            {dayRangeLabel && <span>{dayRangeLabel}</span>}
            {dayRangeLabel && itineraryLabel && (
              <span aria-hidden="true">·</span>
            )}
            {itineraryLabel && <span>{itineraryLabel}</span>}
          </div>
        )}
      </div>

      {(priceLabel || onRemove) && (
        <div className="ml-auto flex shrink-0 items-center gap-3.5">
          {priceLabel && (
            <div className="text-right">
              <div
                data-testid="experience-frame-price"
                className="whitespace-nowrap text-[19px] font-bold text-orange-500"
              >
                {priceLabel}
              </div>

              {pricePerPersonLabel && (
                <div className="whitespace-nowrap text-[12.5px] text-slate-400">
                  {pricePerPersonLabel}
                </div>
              )}
            </div>
          )}

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeLabel}
              title={removeLabel}
              className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border border-solid border-[#E7ECF3] bg-white text-[14px] text-slate-400 transition-colors hover:border-red-200 hover:text-red-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <i className="fa-light fa-xmark" />
            </button>
          )}
        </div>
      )}
    </header>
  ),
);

TripExperienceFrameHeader.displayName = 'TripExperienceFrameHeader';

export default TripExperienceFrameHeader;
